<template>
  <div class="s-blog-search">
    <div class="s-blog-search__container" @mousedown.self="closeSearch">
      <div class="s-blog-search__box">
        <div class="s-blog-search__header">
          <div class="s-blog-search__clear-btn">
            <s-icon class="s-blog-search__search-icon s-hvr-circle-blue" icon-font="s-icon-search" circle/>
          </div>
          <input
            ref="searchInput"
            class="s-blog-search__input"
            placeholder="Search articles, categories, tags and authors"
            v-model="searchQuery"
            @input="handleSearchInput"
          />
          <s-icon class="s-blog-search__close-icon s-hvr-circle-blue" icon-font="s-icon-x-thick" circle @click="closeSearch" />
        </div>

        <div class="s-blog-search__results-container s-scroll">
          <!-- Authors Section -->
          <div v-if="searchQuery && filteredAuthors.length" class="s-blog-search__section">
            <h1 class="s-blog-search__section-title">Authors</h1>
            <div v-for="author in filteredAuthors" :key="author.name" class="s-blog-search__result-item inline-result s-blog-search__author-item">
              <s-icon class="s-blog-search__author-icon s-no-pointer" icon-font="s-icon-profile" circle @click="closeSearch" />
              <h2 class="s-blog-search__author-name">{{ author.name }}</h2>
            </div>
          </div>

          <!-- Categories Section -->
          <div v-if="searchQuery && filteredCategories.length" class="s-blog-search__section">
            <h1 class="s-blog-search__section-title">Categories</h1>
            <div v-for="category in filteredCategories" :key="category" class="s-blog-search__result-item inline-result badge-style s-blog-search__category-item">
              <h2 class="s-blog-search__category-name">{{ category.name }}</h2>
            </div>
          </div>

          <!-- Tags Section -->
          <div v-if="searchQuery && filteredTags.length" class="s-blog-search__section">
            <h1 class="s-blog-search__section-title">Tags</h1>
            <div v-for="tag in filteredTags" :key="tag" class="s-blog-search__result-item inline-result badge-style s-blog-search__tag-item">
              <h2 class="s-blog-search__tag-name">{{ tag.name }}</h2>
            </div>
          </div>

          <!-- Articles Section -->
          <div v-if="searchQuery && searchResults.length" class="s-blog-search__section">
            <h1 class="s-blog-search__section-title">Articles</h1>
            <div
              v-for="article in displayedArticles"
              :key="article.title"
              class="s-blog-search__result-item s-blog-search__article-item"
              @click="navigateToArticle(article)"
            >
              <h2 class="s-blog-search__article-title">
                <span class="s-blog-search__post-title-highlight">{{ article.title }}</span>
              </h2>
              <p class="s-blog-search__article-description">{{ article.preview }}</p>
            </div>
          </div>

          <s-button
            v-if="searchQuery && canShowMore"
            class="s-blog-search__show-more button-primary-dark"
            @click="loadMoreResults"
          >
            Show more results
          </s-button>

          <!-- No Results Section -->
          <div v-if="searchQuery && !filteredAuthors.length && !filteredTags.length && !searchResults.length" class="s-blog-search__section">
            <h2 class="s-blog-search__no-results">No Matching Results Found</h2>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import { navigate } from "vike/client/router";
import { useData } from 'vike-vue/useData';
import SButton from "#root/components/SButton.vue";
import SIcon from "#root/components/SIcon.vue";
import { searchArticles } from '/services/contentService';

// Get initial data from vike
const { categories, tags } = useData();

const searchInput = ref(null);
const searchQuery = ref('');
const searchResults = ref([]);
const limit = ref(5);
const searchTimeout = ref(null);

// For now using mock authors - replace when you have author data
const authors = ref([
  { name: 'John Doe', img: 'https://example.com/john.jpg' },
  { name: 'Jane Smith', img: 'https://example.com/jane.jpg' },
  { name: 'Alice Johnson', img: 'https://example.com/alice.jpg' },
  { name: 'Bob Brown', img: 'https://example.com/bob.jpg' }
]);

onMounted(() => {
  searchInput.value.focus();
});

// Computed properties
const filteredAuthors = computed(() => {
  if (!searchQuery.value) return [];
  return authors.value.filter(author =>
    author.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const filteredTags = computed(() => {
  if (!searchQuery.value || !tags) return [];
  return tags.filter(tag =>
    tag.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const filteredCategories = computed(() => {
  if (!searchQuery.value || !categories) return [];
  return categories.filter(category =>
    category.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const displayedArticles = computed(() => {
  return searchResults.value.slice(0, limit.value);
});

const canShowMore = computed(() => {
  return searchResults.value.length > limit.value;
});

// Methods
const handleSearchInput = async () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  if (!searchQuery.value) {
    searchResults.value = [];
    return;
  }

  searchTimeout.value = setTimeout(async () => {
    try {
      searchResults.value = await searchArticles(searchQuery.value);
    } catch (err) {
      console.error('Search error:', err);
      searchResults.value = [];
    }
  }, 300);
};

const loadMoreResults = () => {
  limit.value += 5;
};

const navigateToArticle = (article) => {
  navigate(`/pulse/articles/${article.id}-${article.slug}`);
  closeSearch();
};

const closeSearch = () => {
  emit('close-search');
};

const emit = defineEmits(['close-search']);
</script>
